/**
 * 캠페인 유형별 요금표 — 계산 규칙과 저장소
 *
 * 화면에서 떼어 둔 이유: 규칙이 돈과 직결돼서 눈으로 읽히고 따로 시험할 수 있어야 한다.
 *
 * ── 왜 표를 둘로 나누는가 ──
 * 지금 계산 규칙이 「통화별」 한 표에 성격이 다른 둘을 같이 담고 있다.
 *   · 환율·현지 VAT·반올림  → 나라가 달라서 바뀌는 값
 *   · 마크업·마진 하한/상한  → 상품이 달라서 바뀌는 값
 * 통화 6개 × 유형 4개 = 24줄이 되고, 태국 환율이 바뀌면 4~5줄을 손으로 고쳐야 한다.
 * 하나라도 빠뜨리면 어떤 유형만 옛날 환율로 견적이 나간다.
 * 그래서 통화표(나라)와 요금표(상품)를 나누고, 계산할 때 둘을 곱한다.
 *
 * ── 실제로 쓰는 계산식 (2026-07-29, 대표님 구글시트에서 역산해 전 줄 검증) ──
 *   VAT        = Original × 현지 VAT율        (나라마다 다름)
 *   환전 수수료 = (Original + VAT) × 수수료율   (최소값 있음, 현지 통화 단위)
 *   인플루언서비 = (Original + VAT + 수수료) × 환율
 *   커미션      = 인플루언서비 × 마크업        (마진 하한/상한 적용)
 *   공급가      = (인플루언서비 + 커미션) → 반올림 단위로 올림
 *
 * ⚠ 서버에 요금표가 아직 없어 이 브라우저에만 저장된다. 서버가 준비되면
 *   아래 읽기·쓰기 함수만 API 호출로 갈아 끼우면 된다.
 *   (2026-08-06 서버 표가 생겼다 — 읽기는 동기로 두고 `hydrateRatePlans()` 가 채운다.
 *    남은 BACKEND-TODO: 캠페인에 「만들 때의 요금표」 스냅샷 보관.)
 */

import { roundToUnit, roundingModeFor } from './roundingMode';
import api from '../api/axios';
import { notifySaveFailure, shouldNotify } from '../utils/saveFailureNotice';

/* 기본값을 바꾸면 키를 올린다 — 브라우저에 저장해 둔 옛 값이 그대로 이기기 때문이다.
   v2: 1:1 마진 하한 450,000 · v3: 바우처 제품 구매 3,000원
   v7: Mid tier enumKey 복구 — 서버엔 있었고 화면 드롭다운에만 빠져 있었다.
   v6: 「♻️ 2차 활용」 유형 제거 — content_reuse 는 캠페인 유형이 아니라 정산 줄 종류였다.
   v5: 버블 원본과 대조해 하한 400,000 · 상한 2,000,000 · 환율(VND/MYR/SGD)을 바로잡음.
       ⚠ 키를 올리므로 이 브라우저에 저장해 둔 요금표 수정은 사라진다 —
         틀린 값을 이어 쓰는 것보다 버블과 맞는 기본값에서 다시 시작하는 편이 안전하다. */
const KEY = 'blb_campaign_rate_plans_v7';

/* ── 나라(통화)에서 오는 값 ──
   VAT는 현지 세금이라 나라마다 다르다. 환전 수수료의 최소값도 현지 통화 단위다.
   지금은 여기 적어 두지만, 서버가 붙으면 기존 「환율 설정」에서 그대로 가져온다. */
/* 환율·VAT 는 2026-07-31 **버블 원본(Client 공급가 계산)** 을 그대로 옮긴 값이다.
   그 전 값(VND 0.058 · MYR 355 · SGD 200)은 어디서 왔는지 모르는 숫자였고,
   특히 싱가포르가 6배 틀려 있었다(버블 1,250). 이 표를 보고 단가를 정하므로 틀리면 안 된다. */
export const COUNTRIES = [
  { k: 'th', label: '🇹🇭 태국',      cur: 'THB', vat: 0.07, feeRate: 0.03, feeMin: 300,    fx: 47 },
  { k: 'vn', label: '🇻🇳 베트남',    cur: 'VND', vat: 0.10, feeRate: 0.03, feeMin: 250000, fx: 0.06 },
  { k: 'my', label: '🇲🇾 말레이시아', cur: 'MYR', vat: 0.08, feeRate: 0.03, feeMin: 40,     fx: 390 },
  { k: 'sg', label: '🇸🇬 싱가포르',   cur: 'SGD', vat: 0,    feeRate: 0.03, feeMin: 15,     fx: 1250 },
];
export const countryOf = (k) => COUNTRIES.find((c) => c.k === k) || COUNTRIES[0];

/* ── 과금 방식 ──
   앞으로 어떤 상품을 만들어도 이 둘 중 하나다. 세 번째가 필요해지면 그때 늘린다. */
export const MODES = {
  markup: '원가 ＋ 마크업',   // 1:1, Mid tier — 인플루언서 단가에 얹는다
  fixed: '고정',              // 시딩 — 단가와 무관하게 인당 정액
};

/* 마진 하한은 유형마다 다르다. 1:1 은 우리가 기획부터 draft 수정까지 다 하니까 높고,
   Mid tier 는 기획안을 그대로 받고 정보 오류만 고치니까 낮다. 그 손품 차이가 곧 이 값이다. */
/* 기본 하한·상한은 **버블 통화 표와 같은 값**이다 — 1:1 Kol 400,000 · 최대 마진 2,000,000
   (2026-07-31 버블 원본 대조). 유형별 요금이 통화 표를 덮으므로 여기가 다르면
   같은 캠페인인데 버블과 새 시스템의 공급가가 갈린다.
   마이그레이션이 끝나기 전까지는 **버블과 같은 금액이 나오는 것이 먼저**다
   (대표님: 「복잡하면 그냥 버블이랑 맞게 맞춰」). 값을 올리려면 버블도 같이 올려야 한다. */
const blankMarkup = (floor = 400000) => Object.fromEntries(
  COUNTRIES.map((c) => [c.k, { rate: 30, floor, cap: 2000000 }]),
);
/* 화면에서도 필요하다 — 고정형 유형(시딩)에는 markup 칸이 아예 없어서,
   과금 방식을 마크업으로 바꾸면 `markup[나라]` 를 읽다가 화면이 통째로 죽었다(2026-08-01).
   기본값을 한 곳에서만 정하려고 여기서 내보낸다. */
export const defaultMarkup = () => blankMarkup();

/* ── 고정형은 「인당 얼마」 하나로 두면 안 된다 ──
   Micro/Reviewer Seeding 의 인당 105,000원은 고정값이 아니라 Micro:Reviewer 가
   5:5 일 때의 가중평균이다(태국 = (150,000+60,000)÷2). 비율은 캠페인마다 달라진다.
   그래서 등급별 단가를 두고, 인당가는 캠페인에서 정한 비율로 그때 계산한다.
   price 가 null 인 나라는 그 상품을 팔지 않는 나라다. */
const blankTiers = () => ([
  { key: 't1', name: '등급 1', price: Object.fromEntries(COUNTRIES.map((c) => [c.k, 100000])) },
]);
/** 등급별 인원으로 인당가를 낸다 — 가중평균 */
export function perHeadOf(tiers, mix, ctry) {
  const rows = (tiers || []).map((t, i) => ({ p: t.price?.[ctry], n: Number((mix || [])[i]) || 0 }))
    .filter((r) => r.p != null && r.n > 0);
  const heads = rows.reduce((a, r) => a + r.n, 0);
  if (!heads) return { per: 0, heads: 0 };
  return { per: rows.reduce((a, r) => a + r.p * r.n, 0) / heads, heads };
}

/* 요금표의 유형 = 캠페인을 만들 때 고르는 「캠페인 타입」이다. 둘이 갈라지면
   「이 캠페인은 어느 요금으로 계산되지?」가 생긴다. 그래서 enumKey 로 묶어 둔다.
   enumKey 는 캠페인 타입의 서버 값(enumMappings.campaignTypeMapping)이다.

   ⚠ enumKey 가 null 인 유형은 캠페인을 만들 때 고를 수 없다. 새로 만든 유형이 그렇다.
     다만 「고를 수 없다」의 원인이 서버라고 단정하면 안 된다 — Mid tier 는 서버에 있는데
     화면 드롭다운(statusOptions.campaignTypeOptions)에만 빠져 있었다(2026-08-01). */
/* 옵션 금액도 나라·상품마다 다르다. null 은 「그 나라·그 상품에서는 제공하지 않음(X)」이다.
   가격표에서 확인한 차이 — 태국 Micro KOL Seeding 은 쇼피 리뷰가 X,
   Giveaway 는 A·B·C 전부 X.
   바우처 제품 구매는 가격표에 5,000(SG 7,000 · MY 6,000)이었지만
   2026-07-29 대표님 지시로 전 나라 3,000 으로 맞춘다. */
/* 옵션은 «key» 로 찾는다. 이름은 사람이 단가표에서 고칠 수 있는 말이고, 이름으로 찾으면
   이름을 고치는 순간 초안검수비가 0원이 된다(draftReviewRateOf 가 이름으로 찾고 있었다).
   key 를 안 적은 옛 표·다른 화면에서 만든 줄은 읽을 때 이름으로 메운다(healPlan). */
const OPT = (name, kind, th, vn, sg, my) => ({ key: name, name, kind, v: { th, vn, sg, my } });
/** 옵션 한 줄을 찾는 열쇠 — key 가 없으면 이름이 곧 key 다 */
export const optKey = (o) => (o && (o.key || o.name)) || '';
/** 단가표에서 「＋ 옵션 추가」를 누르면 만들어지는 줄. 값이 빈 나라는 X 로 뜬다 */
export const newOption = (name = '새 옵션') => ({
  key: 'opt-' + Date.now().toString(36), name, kind: 'per', v: {},
});
/* 단건 진행 +10% 는 **시딩에만 붙는다** (2026-08-14 대표님: 「단건진행은 시딩의 경우만
   10% 추가돼. 중대형이나 미드티어는 그냥 %만 정해져있고」).
   중대형·Mid tier 는 마크업으로만 값을 정하고, 리스트만 받아 가는 건은 따로
   「리스트 제공 비용」으로 받는다(서비스 단가표). 그래서 이 옵션이 그 두 유형에 있으면 안 된다. */
const SINGLE_RUN_NAME = '단건 진행 (6개월 계약이 아닐 때)';
const SINGLE_RUN = () => OPT(SINGLE_RUN_NAME, 'pct', 10, 10, 10, 10);

/* 초안검수는 인당 사람 시간이 드는 일인데 여태 값을 받지 않았다.
   원래 검수가 없는 시딩 유형에도 옵션을 두는 이유 — 「없는 상품인데 클라이언트가
   계속 해달라고 한다」가 이 기능의 출발점이라 켜고 받을 자리가 있어야 한다.

   ── 등급으로 값을 가르지 않는다 (2026-08-25 대표님: 「리뷰어든 마이크로든
   어쨌든 draft 체크는 그렇게 되어있음」) ──
   한때 Reviewer 5,000 · 섞임 7,500 · Micro KOL 10,000 으로 갈라 두었는데,
   초안을 보는 일 자체가 등급에 따라 달라지지 않는다. 시딩은 한 값이다.
   나라 차이만 남긴다 — A·B 옵션이 이미 그 비율로 올라가 있다.

   ── Giveaway 는 팔지 않는다 (같은 날 대표님: 「giveaway는 불가능 모든거 검토
   불가능 선물이니까」) ──
   경품으로 나가는 상품이라 검토할 초안이라는 것이 없다. 값을 비우는 것이 아니라
   줄을 두지 않는다 — 0원은 「공짜로 해 준다」로 읽히고, 없음은 X 로 뜬다.

   **번역은 포함하지 않는다.** 현지어 초안을 클라이언트가 읽도록 옮겨 주는 일이 아니라,
   가이드라인(제품명·필수 문구·해시태그·노출 컷)과 대조해 우리가 확인하고 수정 요청을
   보내는 일이다. 그래서 값이 A·B 옵션 수준에 머문다 — 번역까지 하면 이 값으로 안 된다. */
const DRAFT_REVIEW_NAME = 'D 초안검수';
/** 시딩 공통 단가. MY·SG 는 A·B 옵션과 같은 비율로 올린다 */
const DRAFT_REVIEW_SEEDING = () => OPT(DRAFT_REVIEW_NAME, 'per', 5000, 5000, 7000, 6000);
/* 마크업형(중대형·Mid tier)은 초안 수정이 이미 마진 하한에 들어 있다(work.draft = free/factonly).
   같은 일을 옵션으로 또 받으면 두 번 청구가 된다 — 값을 바꾸기 전에 대표님 확인이 필요해
   여기만 옛 10,000 을 그대로 둔다. */
const DRAFT_REVIEW = () => OPT(DRAFT_REVIEW_NAME, 'per', 10000, 10000, 10000, 10000);
/** 자리만 잡아 두었던 옛 값 — 이 값일 때만 유형별 단가로 바꾼다(healPlan) */
const DRAFT_REVIEW_PLACEHOLDER = 10000;

/* 화면이 이 두 줄만은 이름이 아니라 열쇠로 알아야 한다 — 단가표에서 자기 자리가 따로 있다 */
export const SINGLE_RUN_KEY = SINGLE_RUN_NAME;
export const DRAFT_REVIEW_KEY = DRAFT_REVIEW_NAME;

export const DEFAULT_PLANS = [
  {
    id: 'p-mega', enumKey: 'mega_macro_kol', name: '⭐ Mega&Macro KOL', mode: 'markup',
    /* 버블 통화 표의 「최소 마진(1:1 Kol)」 = 400,000. 한동안 450,000 으로 적혀 있었는데
       근거를 찾을 수 없었고, 그 값이면 같은 캠페인이 버블보다 5만원 비싸게 나갔다. */
    markup: blankMarkup(400000),
    opts: [DRAFT_REVIEW()],   // 단건 진행 +10% 는 시딩만 — 위 SINGLE_RUN 주석
    work: { plan: 'ours', check: true, draft: 'free', report: true },
  },
  {
    /* Mid tier 는 **서버에 등록돼 있다**(enumMappings.campaignTypeMapping — 「서버 등록 완료」).
       한동안 enumKey 를 null 로 내려 「⚠ 서버 등록 전」이 떴는데, 그건 화면의 드롭다운
       (statusOptions.campaignTypeOptions)에 이 유형만 빠져 있어서였다. 서버 문제가 아니었다. */
    id: 'p-mid', enumKey: 'mid_tier', name: 'Mid tier', mode: 'markup',
    /* 마진 하한 **200,000** — 대표님 지시(2026-08-04). 1:1(400,000)의 절반이다.
       Mid tier 는 기획안을 그대로 받고 정보 오류만 고치니 손품이 적다(위 주석 참고).
       버블에는 없는 유형이라 「버블과 같은 금액이 나와야 한다」가 걸리지 않는다 —
       Mid tier 는 신 SaaS 에서만 판다. */
    markup: blankMarkup(200000),
    opts: [DRAFT_REVIEW()],   // 단건 진행 +10% 는 시딩만 — 위 SINGLE_RUN 주석
    work: { plan: 'client', check: true, draft: 'factonly', report: true },
  },
  {
    id: 'p-micro', enumKey: 'micro_kol_seeding', name: '🔥 Micro KOL Seeding', mode: 'fixed',
    /* 고정형이라 인당 단가로 팔지만, **KOL 한 명 한 명의 공급가를 계산할 때는** 마진 하한이 필요하다.
       그 값이 나라·통화 표의 「최소 마진(Seeding)」에 있었는데, 유형이 늘어도 칸을 못 늘리는
       구조라 여기로 옮겼다(2026-07-31). 싱가포르만 하한이 높다 — 원래 통화 표가 그랬다. */
    margin: { th: { floor: 50000, cap: 2000000 }, vn: { floor: 50000, cap: 2000000 },
      my: { floor: 50000, cap: 2000000 }, sg: { floor: 150000, cap: 2000000 } },
    tiers: [{ key: 'micro', name: 'Micro KOL', price: { th: 150000, vn: 150000, sg: null, my: null } }],
    mix: [30],
    minHeads: { th: 30, vn: 30, sg: null, my: null },
    guarantee: { th: 100, vn: 100, sg: null, my: null },
    /* 값의 정본은 **레이트카드**다(ratecard.blbent.com · 2026-08-13 대표님: 「레이트카드에
       맞출게」). 태국 Micro KOL Seeding 도 카드에는 A·B·C 가 다 붙어 있다 —
       여기 「태국만 X」라고 적혀 있던 것은 카드와 달랐다. */
    opts: [
      SINGLE_RUN(),
      OPT('A 바우처로 제품 구매', 'per', 5000, 5000, null, null),
      OPT('B 쇼피 리뷰', 'per', 2500, 2500, null, null),
      OPT('C 오프라인 현장 방문', 'per', 50000, 50000, null, null),
      DRAFT_REVIEW_SEEDING(),
    ],
    work: { plan: 'none', check: true, draft: 'none', report: true },
  },
  {
    id: 'p-micro-rev', enumKey: 'micro_reviewer_seeding', name: '🌱 Micro/Reviewer Seeding', mode: 'fixed',
    /* 고정형이라 인당 단가로 팔지만, **KOL 한 명 한 명의 공급가를 계산할 때는** 마진 하한이 필요하다.
       그 값이 나라·통화 표의 「최소 마진(Seeding)」에 있었는데, 유형이 늘어도 칸을 못 늘리는
       구조라 여기로 옮겼다(2026-07-31). 싱가포르만 하한이 높다 — 원래 통화 표가 그랬다. */
    margin: { th: { floor: 50000, cap: 2000000 }, vn: { floor: 50000, cap: 2000000 },
      my: { floor: 50000, cap: 2000000 }, sg: { floor: 150000, cap: 2000000 } },
    /* 인당가가 비율에 따라 달라지는 유일한 상품. 5:5 가 기본이고 캠페인마다 조정한다.
       태국 5:5 = (150,000+60,000)÷2 = 105,000 · 베트남 5:5 = (150,000+50,000)÷2 = 100,000 */
    tiers: [
      { key: 'micro', name: 'Micro KOL', price: { th: 150000, vn: 150000, sg: null, my: null } },
      { key: 'reviewer', name: 'Reviewer', price: { th: 60000, vn: 50000, sg: null, my: null } },
    ],
    mix: [15, 15],
    minHeads: { th: 30, vn: 30, sg: null, my: null },
    guarantee: { th: 100, vn: 100, sg: null, my: null },
    /* 값의 정본은 레이트카드다 — 태국·베트남 A 5,000 · B 2,500 · C 50,000 */
    opts: [
      SINGLE_RUN(),
      OPT('A 바우처로 제품 구매', 'per', 5000, 5000, null, null),
      OPT('B 쇼피 리뷰', 'per', 2500, 2500, null, null),
      OPT('C 오프라인 현장 방문', 'per', 50000, 50000, null, null),
      DRAFT_REVIEW_SEEDING(),
    ],
    work: { plan: 'none', check: true, draft: 'none', report: true },
  },
  {
    id: 'p-rev', enumKey: 'reviewer_seeding', name: '✍️ Reviewer Seeding', mode: 'fixed',
    /* 고정형이라 인당 단가로 팔지만, **KOL 한 명 한 명의 공급가를 계산할 때는** 마진 하한이 필요하다.
       그 값이 나라·통화 표의 「최소 마진(Seeding)」에 있었는데, 유형이 늘어도 칸을 못 늘리는
       구조라 여기로 옮겼다(2026-07-31). 싱가포르만 하한이 높다 — 원래 통화 표가 그랬다. */
    margin: { th: { floor: 50000, cap: 2000000 }, vn: { floor: 50000, cap: 2000000 },
      my: { floor: 50000, cap: 2000000 }, sg: { floor: 150000, cap: 2000000 } },
    /* 싱가포르·말레이시아는 「KOC Reviewer Seeding」 이라는 이름으로 이 상품만 판다.
       베트남은 단독 상품으로는 팔지 않지만, Micro/Reviewer 가중평균을 맞추려면 50,000 이 맞다. */
    tiers: [{ key: 'reviewer', name: 'Reviewer', price: { th: 60000, vn: 50000, sg: 170000, my: 105000 } }],
    mix: [50],
    minHeads: { th: 50, vn: 30, sg: 30, my: 30 },
    guarantee: { th: 90, vn: 100, sg: 100, my: 100 },
    /* 값의 정본은 레이트카드다. 이 상품만 나라마다 A·B 가 다르다 —
       말레이시아·싱가포르는 카드에 「KOC Reviewer Seeding」 이라는 이름으로 실려 있다. */
    opts: [
      SINGLE_RUN(),
      OPT('A 바우처로 제품 구매', 'per', 5000, 5000, 7000, 6000),
      OPT('B 쇼피 리뷰', 'per', 2500, 2500, 4000, 2500),
      OPT('C 오프라인 현장 방문', 'per', 50000, 50000, 50000, 50000),
      DRAFT_REVIEW_SEEDING(),
    ],
    work: { plan: 'none', check: true, draft: 'none', report: true },
  },
  {
    id: 'p-give', enumKey: 'giveaway_seeding', name: '🎁 Giveaway Seeding', mode: 'fixed',
    /* 고정형이라 인당 단가로 팔지만, **KOL 한 명 한 명의 공급가를 계산할 때는** 마진 하한이 필요하다.
       그 값이 나라·통화 표의 「최소 마진(Seeding)」에 있었는데, 유형이 늘어도 칸을 못 늘리는
       구조라 여기로 옮겼다(2026-07-31). 싱가포르만 하한이 높다 — 원래 통화 표가 그랬다. */
    margin: { th: { floor: 50000, cap: 2000000 }, vn: { floor: 50000, cap: 2000000 },
      my: { floor: 50000, cap: 2000000 }, sg: { floor: 150000, cap: 2000000 } },
    /* Reviewer 나 Micro KOL Seeding 을 할 때 얹는 상품이다. 단독으로는 팔지 않는다.
       Draft·리포트·업로드 보장이 없어서 값이 싸다. */
    tiers: [{ key: 'giveaway', name: 'Giveaway', price: { th: 30000, vn: 30000, sg: null, my: null } }],
    mix: [50],
    minHeads: { th: 50, vn: 50, sg: null, my: null },
    guarantee: { th: null, vn: null, sg: null, my: null },   // 보장 없음(평균 70% 내외)
    addOn: true,
    /* 초안검수는 없다 — 경품이라 검토할 초안이 없다(2026-08-25 대표님) */
    opts: [SINGLE_RUN()],
    work: { plan: 'none', check: false, draft: 'none', report: false },
  },
  /* ⚠ 「♻️ 2차 활용」 유형은 뺐다(2026-07-31).
     한동안 `enumKey: 'content_reuse'` 로 넣어 뒀는데, `content_reuse` 는 **캠페인 유형이 아니라
     정산 줄의 종류**였다(statusOptions.settlementTypeOptions — 이 줄이 캠페인 건인지 2차 활용 건인지).
     캠페인을 만들 때 고르는 유형은 campaignTypeOptions 의 다섯 개뿐이고 2차 활용은 없다(버블도 같다).
     그래서 이 유형은 어떤 캠페인에도 안 걸리는 죽은 줄이었고, 목록에서는 고를 수 있는 것처럼 보였다.
     2차 활용의 마진 하한(30,000)은 **부가 항목 기준**(config/addonItems)으로 옮겼고,
     실제 작업은 캠페인 안의 Content Reuse 탭에서 한다. */
];

/* 화면이 읽다가 죽지 않게 빠진 칸을 채워 준다.
   과금 방식을 고정형 → 마크업으로 바꾸면 markup 칸이 없는 채로 저장될 수 있었고,
   그 상태로 다시 열면 `markup[나라]` 를 읽다가 화면 전체가 하얘졌다(2026-08-01).
   저장된 값을 버리지 않고 여기서 메워야 이미 깨진 브라우저도 살아난다. */
/* 나라 칸이 «하나만» 비어도 같은 죽음이 온다 — 화면은 COUNTRIES 전부를 줄로 그리면서
   `cur.markup[나라].rate` 를 읽는다(CampaignRatePlanSection 159·321·349·542).
   저장된 표에는 그 칸이 없을 수 있다:
     · 나라를 새로 늘렸는데 저장된 표는 옛 네 나라뿐일 때
     · 서버 표를 만든 쪽이 일부 나라만 채워 보냈을 때 (서버가 정본이 된 지금 새로 열린 길)
   빈 칸만 메운다. **적힌 값은 절대 덮지 않는다**(인계서 규칙 ②).
   메우는 값은 그 유형이 이미 쓰는 값을 따른다 — 유형마다 하한이 다른데(1:1 400,000 ·
   Mid tier 200,000) 일률적인 기본값을 넣으면 그 나라만 다른 장사 규칙이 된다. */
const fillCountries = (byCountry, fallback) => {
  const known = Object.values(byCountry || {}).find((v) => v && typeof v === 'object');
  const out = { ...(byCountry || {}) };
  COUNTRIES.forEach((c) => {
    if (!out[c.k] || typeof out[c.k] !== 'object') out[c.k] = { ...(known || fallback) };
  });
  return out;
};

const healPlan = (p) => {
  if (!p || typeof p !== 'object') return p;
  let next = p;
  /* 옵션에 key 를 메운다. key 가 생기기 전에 저장된 표와, 「캠페인 유형별 요금」 화면에서
     만든 줄에는 key 가 없다. 이름이 곧 key 였던 시절의 이름을 그대로 열쇠로 삼는다 —
     그래야 나중에 이름을 고쳐도 초안검수비를 계속 찾아낸다. */
  if (Array.isArray(next.opts) && next.opts.some((o) => o && !o.key && o.name)) {
    next = { ...next, opts: next.opts.map((o) => ((o && !o.key && o.name) ? { ...o, key: o.name } : o)) };
  }
  if (p.mode !== 'fixed') {
    next = { ...next, markup: fillCountries(p.markup, { rate: 30, floor: 400000, cap: 2000000 }) };
    /* 마크업형(중대형·Mid tier)에는 단건 진행 +10% 가 없다 — 시딩만 붙는 값이다(2026-08-14 대표님).
       기본표에서 뺀 것만으로는 부족하다: 이미 저장된 표(브라우저·서버)에는 그 줄이 남아 있어
       고치기 전과 똑같이 화면에 뜨고 견적에 붙는다. 읽을 때 걷어내야 모든 PC 가 같이 바뀐다.
       ⚠ 다른 값은 손대지 않는다 — 적힌 값은 덮지 않는 것이 이 표의 규칙이다. */
    if (Array.isArray(next.opts) && next.opts.some((o) => o && optKey(o) === SINGLE_RUN_NAME)) {
      next = { ...next, opts: next.opts.filter((o) => !(o && optKey(o) === SINGLE_RUN_NAME)) };
    }
  }
  /* 고정형도 KOL 한 명의 공급가를 낼 때 margin[나라] 를 읽는다 */
  if (p.mode === 'fixed' && p.margin) {
    next = { ...next, margin: fillCountries(p.margin, { floor: 50000, cap: 2000000 }) };
  }
  if (p.mode === 'fixed' && !(p.tiers || []).length) {
    next = { ...next, tiers: blankTiers(), mix: [30] };
  }
  /* 초안검수 단가 — 이미 저장된 표(브라우저·서버)에는 자리만 잡아 둔 10,000 이
     유형 구분 없이 들어 있다. 기본표만 고치면 저장된 PC 는 옛 값으로 견적을 낸다.
     그래서 읽을 때 맞춘다. 단 **옛 값 그대로일 때만** — 사람이 단가표에서 고쳐 둔 값은
     덮지 않는다(적힌 값은 덮지 않는 것이 이 표의 규칙이다). 줄이 아예 없으면 넣어 준다.
     시딩(고정형)만 손댄다 — 중대형·Mid tier 는 초안 수정이 마진 하한에 이미 들어 있어
     값을 바꾸려면 대표님 확인이 먼저다. */
  if (p.mode === 'fixed') {
    const known = DEFAULT_PLANS.find((d) => d.id === p.id);
    const std = ((known || {}).opts || []).find((o) => optKey(o) === DRAFT_REVIEW_NAME);
    /* 안 파는 유형에서는 줄을 걷어낸다 — Giveaway 는 경품이라 검토할 초안이 없다
       (2026-08-25 대표님). 기본표에서 뺀 것만으로는 부족하다: 저장된 표에 남은 줄이
       그대로 화면에 뜨고 견적에 붙는다. 단건 진행을 걷어내는 자리와 같은 이유다.
       ⚠ 요금표에 없는(사람이 새로 만든) 유형은 건드리지 않는다. */
    if (known && !std && (next.opts || []).some((o) => o && optKey(o) === DRAFT_REVIEW_NAME)) {
      next = { ...next, opts: next.opts.filter((o) => !(o && optKey(o) === DRAFT_REVIEW_NAME)) };
    }
    if (std) {
      const mine = (next.opts || []).find((o) => o && optKey(o) === DRAFT_REVIEW_NAME);
      const fresh = () => JSON.parse(JSON.stringify(std));
      if (!mine) {
        next = { ...next, opts: [...(next.opts || []), fresh()] };
      } else if (COUNTRIES.every((c) => {
        const v = (mine.v || {})[c.k];
        return v == null || v === DRAFT_REVIEW_PLACEHOLDER;
      })) {
        next = { ...next, opts: next.opts.map((o) => (o && optKey(o) === DRAFT_REVIEW_NAME ? fresh() : o)) };
      }
    }
  }
  return next;
};

/* 유형 하나가 «객체»인가. healPlan 은 빈 칸을 메우는 일이라 유형이 통째로 뭉개진 것은
   못 살린다 — 이름도 enumKey 도 없는 유형이 되어 화면에는 「⚠ 서버 등록 전」 유령 줄로 남고,
   견적은 그 표를 그대로 쓴다. 2026-08-06~08-10 에 실제로 일어났다: 서버에 저장된 표가
   `[[],[],[],[],[],[]]` 이었고, 그 표를 받아온 브라우저는 자기 표를 그것으로 덮었다.
   캠페인 만들기 화면에 「· 서버 등록 전」 유령 유형 여섯 개가 떴다.
   어디서 뭉갠 것인지는 서버 쪽에서 아직 못 짚었다 — 그래서 화면도 스스로 막는다. */
const isPlan = (p) => !!p && typeof p === 'object' && !Array.isArray(p)
  && (p.id !== undefined || p.name !== undefined || p.mode !== undefined);

/* 요금표로 쓸 수 있는 모양인가. 빈 표(길이 0)와 «뭉개진 표»를 같이 막는다.
   요금표는 돈이다 — 이상하면 쓰지 않고 화면 기본표로 간다(인계서 규칙 ⑤). */
export const usableTable = (v) => Array.isArray(v) && v.length > 0 && v.every(isPlan);

export const loadPlans = () => {
  try {
    const r = localStorage.getItem(KEY);
    const v = r ? JSON.parse(r) : null;
    /* 뭉개진 표가 이미 이 브라우저에 저장돼 있어도 여기서 되살아난다 — 사람이 저장소를
       손으로 지우지 않아도 새로고침 한 번이면 정상 표로 돌아온다. */
    if (!usableTable(v)) return JSON.parse(JSON.stringify(DEFAULT_PLANS));
    return v.map(healPlan);
  } catch (e) { return JSON.parse(JSON.stringify(DEFAULT_PLANS)); }
};
/* { onSaved, onFail } — 서버가 받았을 때·끝내 못 받았을 때 화면에 알린다(2026-10-16 확인창 점검: 실패하면 이 PC 의 저장값만
   되돌리고 화면(React 상태)은 새 값 그대로라, 새로고침 전까지 서버와 다른 단가가 보였다). 저장되는 값·계산은 그대로다. */
export const savePlans = (v, { onSaved, onFail } = {}) => {
  /* 빈 표를 저장하지 않는다 — 화면 오류로 한 번 날아오는 것만으로 전 유형의 단가가 사라진다.
     서버도 같은 것을 막지만, 브라우저 값이 먼저 지워지면 새로고침 한 번에 되살릴 길이 없다. */
  if (!usableTable(v)) return false;
  let before = null;
  try { before = localStorage.getItem(KEY); localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* 저장 실패해도 화면은 돈다 */ }
  /* 서버로는 뒤에서 보낸다. 배포 전이면 404 가 오고, 그때는 예전처럼 브라우저만 쓴다.
     그 밖의 실패(확인 창 취소·권한 없음·서버 오류)는 알리고 이 PC 의 표를 저장 전으로 되돌린다
     (2026-10-02 저장 최종점검 S4) — 예전엔 삼켜서 «저장된 것처럼» 보이다 새로고침 때 사라졌다. */
  api.put('/api/campaign-rate-plans', { version: KEY, plans: v, countries: COUNTRIES })
    .then(() => { if (onSaved) onSaved(v); })
    .catch((error) => {
      if (!shouldNotify(error)) return;
      if (onFail) onFail(v, error);
      try {
        if (before === null) localStorage.removeItem(KEY); else localStorage.setItem(KEY, before);
      } catch (e) { /* 되돌리기 실패 — 알림은 띄운다 */ }
      notifySaveFailure('요금표', error, '이 PC 의 요금표도 저장 전으로 되돌렸습니다. 새로고침 후 다시 저장해 주세요.');
    });
  return true;
};

/**
 * 요금표를 «한 번에» 저장한다 — 수정을 다 마친 뒤 저장 단추를 눌렀을 때(2026-10-23 사용자).
 * `savePlans` 는 칸을 고칠 때마다 이 PC 에 먼저 적고 서버로는 뒤에서 보냈다. 이 함수는 순서가 반대다:
 * **서버가 받은 뒤에만** 이 PC 값을 바꾼다. 확인 창을 취소하거나 서버가 거부하면 이 PC 의 저장값은 그대로이고
 * (견적 계산은 계속 저장된 표를 쓴다), 호출한 화면이 고치던 값을 그대로 들고 있다.
 * @param {object[]} v 저장할 표
 * @param {object} [session] askCriticalConfirm 이 돌려준 확인 — 있으면 확인 창을 또 띄우지 않는다
 * @throws 서버가 거부했거나(확인 취소 포함) 닿지 않았을 때. 404(서버가 아직 모름)는 예전처럼 이 PC 에만 저장한다.
 */
export const commitPlans = async (v, session) => {
  if (!usableTable(v)) throw new Error('요금표가 비어 있거나 모양이 올바르지 않아 저장하지 않았습니다');
  try {
    const payload = { version: KEY, plans: v, countries: COUNTRIES };
    if (session) await api.put('/api/campaign-rate-plans', payload, { criticalSession: session });
    else await api.put('/api/campaign-rate-plans', payload);
  } catch (error) {
    if (error?.response?.status !== 404) throw error;
  }
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* 저장 실패해도 화면은 돈다 */ }
  return true;
};

/**
 * 서버에 저장된 요금표로 브라우저 값을 갱신한다. 요금표를 쓰는 화면이 열릴 때 한 번 부른다.
 *
 * 화면 열 곳이 `loadPlans()` 를 **기다리지 않고 바로** 부르기 때문에 그 함수는 동기로 두고,
 * 서버 값은 이 함수가 미리 받아 브라우저에 채워 넣는다.
 *
 * 규칙 둘:
 *  · **버전이 다르면 서버 값을 쓰지 않는다.** 기본 요금표 계산이 바뀌었는데 옛 표가
 *    그대로 이기면 같은 캠페인이 버블과 다른 금액으로 나간다(그래서 KEY 에 버전이 있다).
 *  · 서버에 아직 아무것도 없으면 **지금 이 브라우저의 표를 한 번 올려 준다** — 첫 배포 때
 *    누군가 손으로 고쳐 둔 값을 잃지 않기 위해서다.
 *
 * @returns {Promise<boolean>} 서버 값으로 갱신했으면 true
 */
export const hydrateRatePlans = async () => {
  try {
    const { data } = await api.get('/api/campaign-rate-plans');
    const body = (data && data.data !== undefined && data.success !== undefined) ? data.data : data;

    if (!body || body.exists === false) {
      const mine = loadPlans();
      if (Array.isArray(mine) && mine.length) {
        await api.put('/api/campaign-rate-plans', { version: KEY, plans: mine, countries: COUNTRIES });
      }
      return false;
    }

    if (body.version !== KEY) {
      console.warn(
        `요금표 버전이 다릅니다(서버 ${body.version} · 화면 ${KEY}). 화면의 기본 요금표를 씁니다.`,
      );
      return false;
    }

    /* 서버 표가 «쓸 수 있는 모양»일 때만 브라우저 값을 덮는다.
       빈 표만 막던 자리다. 그런데 2026-08-06 에 저장된 서버 표는 비어 있지 않았고
       `[[],[],[],[],[],[]]` — 유형 여섯이 통째로 뭉개진 표였다.
       길이 검사만으로는 통과해서, 이 표가 각 PC 의 정상 표를 덮어썼다.
       이상하면 «덮지 않고» 이 브라우저 값으로 계속한다 — 요금표는 돈이다. */
    if (!usableTable(body.plans)) {
      console.warn(
        '서버 요금표가 쓸 수 없는 모양이라 쓰지 않았습니다. 이 브라우저 값으로 계속합니다.',
        body.plans,
      );
      return false;
    }
    try { localStorage.setItem(KEY, JSON.stringify(body.plans)); } catch (e) { /* 무시 */ }
    return true;
  } catch (error) {
    if (error?.response?.status !== 404) {
      console.warn('요금표를 서버에서 가져오지 못했습니다. 이 브라우저 값으로 계속합니다.', error);
    }
    return false;
  }
};

/* 새로 만든 유형은 서버 캠페인 타입에 아직 없으므로 enumKey 가 비어 있다.
   서버에 값이 생기기 전까지는 캠페인을 만들 때 고를 수 없다. */
export const newPlan = (name = '새 유형') => ({
  id: `p-${Date.now().toString(36)}`, enumKey: null, name, mode: 'markup',
  markup: blankMarkup(), tiers: blankTiers(), mix: [30],
  minHeads: Object.fromEntries(COUNTRIES.map((c) => [c.k, 30])),
  guarantee: Object.fromEntries(COUNTRIES.map((c) => [c.k, 100])),
  opts: [], work: { plan: 'ours', check: true, draft: 'free', report: true },
});

/* ── 계산 ── */
export const numOf = (s) => Number(String(s).replace(/[^0-9.-]/g, '')) || 0;
export const won = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');
export const dec1 = (n) => (Number(n) || 0).toLocaleString('ko-KR', { maximumFractionDigits: 1 });
/** 0.07 → "7" — 소수 계산에서 7.000000000000001 이 나오는 걸 막는다 */
export const pct = (r) => Number((Number(r) * 100).toFixed(3)).toString();

/** 인플루언서 단가(현지통화) → 우리가 실제로 지급하는 원화 */
export function kolCostKRW(orig, ctry, fx) {
  const c = countryOf(ctry);
  const o = Number(orig) || 0;
  const vat = o * c.vat;
  const fee = Math.max(c.feeMin, (o + vat) * c.feeRate);
  return { orig: o, vat, fee, krw: (o + vat + fee) * Number(fx || c.fx) };
}

/** 마크업형 — 마진이 하한 미만이면 하한, 상한 초과면 상한 */
export function commissionOf(kolKRW, m) {
  const raw = kolKRW * (Number(m.rate) || 0) / 100;
  const value = Math.min(Math.max(raw, m.floor), m.cap);
  return { raw, value, hit: raw < m.floor ? 'floor' : raw > m.cap ? 'cap' : 'rate' };
}

/** 클라이언트 공급가 — 천원 단위로 맞춘다(세금계산서와 어긋나지 않게).
    방식은 나라·통화별 반올림 설정을 따른다 — 기본 반올림, 고른 통화만 올림(2026-09-30 대표 결정).
    그전에는 여기만 따로 «천원 단위 올림» 으로 고정돼 있었다. */
export const ROUND_TO = 1000;
export const roundSupply = (v, mode = 'round') => roundToUnit(v, ROUND_TO, mode);
/** 이 나라(k: 'th' 등)의 공급가 단위 맞춤 */
export const roundSupplyFor = (v, ctryKey) => roundSupply(v, roundingModeFor(countryOf(ctryKey).cur));
/** 원가 + 마진을 맞추되, 반올림이 마진을 하한 아래로 끌어내리면 그 건만 올림(반올림 하한 보호, 2026-09-30).
    상한이 걸린 줄(capped)은 보호하지 않는다 — 상한 우선. */
export const roundSupplyKeepFloor = (cost, margin, floor, ctryKey, capped = false) => {
  const v = (Number(cost) || 0) + (Number(margin) || 0);
  const r = roundSupplyFor(v, ctryKey);
  if (!capped && floor != null && r - cost < Number(floor) - 1e-6) return roundSupply(v, 'ceil');
  return r;
};

/** 한 줄 전체 — 단가 하나로 공급가까지 */
export function quoteRow(orig, plan, ctry, fx) {
  const k = kolCostKRW(orig, ctry, fx);
  const m = (plan.markup || {})[ctry] || { rate: 0, floor: 0, cap: Infinity };
  const c = commissionOf(k.krw, m);
  return { ...k, raw: c.raw, com: c.value, hit: c.hit, supply: roundSupplyKeepFloor(k.krw, c.value, m.floor, ctry, c.hit === 'cap') };
}

/** 마크업 마진과 하한이 같아지는 단가 — 이 아래는 하한이, 위는 마크업이 가격을 정한다 */
export const turnPoint = (m) => (m.rate ? Math.round(m.floor / (m.rate / 100)) : 0);
export const capPoint = (m) => (m.rate ? Math.round(m.cap / (m.rate / 100)) : 0);

export const HIT_LABEL = { floor: '하한', rate: '마크업', cap: '상한' };
export const HIT_COLOR = { floor: '#D9A15A', rate: '#D97757', cap: '#4CAE7C' };

export const WORK_PLAN = { ours: '우리가 협의해서 제작', client: '클라이언트 기획안 그대로', none: '없음' };
export const WORK_DRAFT = { free: '있음 — 자유롭게 수정', factonly: '있음 — 잘못된 정보만 수정', none: '없음' };

/* 캠페인을 만들 때 초안검수 스위치의 기본값.
   유형마다 「초안 수정을 해주는 상품인가」가 work.draft 에 이미 적혀 있어서 그대로 쓴다.
   캠페인에 따로 기본값을 두면 요금표와 캠페인이 서로 다른 말을 하게 된다. */
export const draftReviewDefault = (plan) =>
  !!(plan && plan.work && plan.work.draft && plan.work.draft !== 'none');

/* 캠페인의 유형값으로 요금표 줄을 찾는다.
   서버는 enum(mega_macro_kol)으로 주고, 화면은 라벨(⭐ Mega&Macro KOL)로 다닌다.
   게다가 라벨의 이모지·구분자가 자료마다 다르다 — 실데이터엔 🌟 와 & 가,
   매핑표엔 ⭐ 와 / 가 들어 있다. 그래서 enum 을 먼저 보고,
   라벨로 들어온 경우에만 글자만 남겨서 비교한다. */
const alphaOnly = (s) => String(s).toLowerCase().replace(/[^a-z]/g, '');

export const findPlanByCampaignType = (plans, campaignType) => {
  if (!campaignType) return null;
  const list = plans && plans.length ? plans : DEFAULT_PLANS;
  const byEnum = list.find((p) => p.enumKey && p.enumKey === campaignType);
  if (byEnum) return byEnum;
  const target = alphaOnly(campaignType);
  return list.find((p) => alphaOnly(p.name) === target) || null;
};

/** 그 나라의 초안검수 인당 단가. 옵션이 없거나 그 나라에서 안 파는 상품이면 null. */
export const draftReviewRateOf = (plan, ctry) => {
  /* 이름이 아니라 key 로 찾는다 — 단가표에서 이름을 고쳐도 검수비가 사라지면 안 된다 */
  const opt = ((plan && plan.opts) || []).find((o) => optKey(o) === DRAFT_REVIEW_NAME);
  if (!opt) return null;
  const v = (opt.v || {})[ctry];
  return v == null ? null : v;
};
